// src/lib/fishiq/fishIQMovementRead.ts
// Forecast/read only. Confidence is capped by the evidence trace.

import type { FishIQSpeciesPrediction, FishIQTacticalFusion, FishIQWaterProfile } from '../../types';
import type { FishIQEvidenceTrace } from './fishIQEvidence';
import { buildFishIQSpeciesConfidence } from './fishIQConfidenceModel';

const clean = (value: any) => String(value ?? '').trim();
const lower = (value: any) => clean(value).toLowerCase();
const list = (value: any): any[] => (Array.isArray(value) ? value : value == null ? [] : [value]);
const unique = (items: string[]) => Array.from(new Set(items.map(clean).filter(Boolean)));

export type FishIQMovementReadInput = {
  profile: FishIQWaterProfile;
  species?: any[] | null;
  structureTags?: string[] | null;
  evidence?: FishIQEvidenceTrace | null;
  environment?: any | null;
};

export function fishIQSpeciesEmoji(species: any): string {
  const hay = [species?.commonName, species?.name, species?.id, species?.category, species?.summary].map(lower).join(' ');
  if (/crab|shrimp|lobster|crawfish|crayfish/.test(hay)) return '🦀';
  if (/clam|oyster|mussel|scallop/.test(hay)) return '🐚';
  if (/squid|octopus/.test(hay)) return '🦑';
  if (/shark|ray|skate/.test(hay)) return '🦈';
  return /trout|salmon|steelhead|char|bass|bluegill|perch|pike|walleye|catfish|carp|drum|snapper|grouper|halibut|flounder|tuna|mahi|marlin|sailfish/.test(hay) ? '🐟' : '🐠';
}

export function buildFishIQBestWindow(profile: FishIQWaterProfile, species?: any[] | null): string {
  const top = Array.isArray(species) ? species.slice(0, 5) : [];
  const speciesWindow = top.flatMap((item) => [...list(item?.bestTimesOfDay), ...list(item?.tactics?.bestTimesOfDay), ...list(item?.activeTimes)]).map(clean).find(Boolean);
  if (speciesWindow) return speciesWindow;
  if (profile.timeOfDay === 'dawn') return 'Low light window now';
  if (profile.timeOfDay === 'dusk') return 'Evening low light window';
  if (profile.timeOfDay === 'night') return 'Night / dock-light edges';
  if (profile.waterType === 'saltwater' || profile.waterType === 'brackish') return 'Moving water / tide-change window';
  if (profile.season === 'summer') return 'Early, late, and shade lines';
  return 'Dawn, dusk, and active weather changes';
}

const prettyStructure = (value: any) => clean(value).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

const sentence = (value: string) => {
  const text = clean(value);
  if (!text) return '';
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

export function buildFishIQTacticalFusion(input: FishIQMovementReadInput): FishIQTacticalFusion | null {
  const environment = input.environment || null;
  if (!environment) return null;

  const current = environment?.current?.available === true ? environment.current : null;
  const bathymetry = environment?.bathymetry?.available === true ? environment.bathymetry : null;
  const weatherAvailable = Boolean(environment?.weather);
  const weatherSignal = environment?.weatherSignal || null;
  const active = Boolean(current || bathymetry || weatherAvailable);
  if (!active) return null;

  const confidence = Math.max(0, Math.min(1, Number(environment?.environmentConfidence || 0)));
  const confidenceLabel: FishIQTacticalFusion['confidenceLabel'] =
    confidence >= 0.72 ? 'strong' : confidence >= 0.5 ? 'good' : 'limited';

  const speed = Number(current?.speedKnots);
  const direction = clean(current?.directionLabel);
  const strength = lower(current?.strength);
  const phase = clean(current?.phase);
  const trend = clean(current?.trend);
  const currentFact = current
    ? [
        Number.isFinite(speed) ? `${speed < 1 ? speed.toFixed(2) : speed.toFixed(1)} kt` : '',
        direction,
      ].filter(Boolean).join(' ')
    : '';

  const depth = Number(bathymetry?.depthMeters);
  const relief = Number(bathymetry?.localReliefMeters);
  const slope = Number(bathymetry?.slopeDegrees);
  const slopeClass = prettyStructure(bathymetry?.slopeClass);
  const envStructures = Array.isArray(bathymetry?.structureTypes)
    ? bathymetry.structureTypes.map(prettyStructure).filter((v: string) => v && lower(v) !== 'unknown')
    : [];
  const inputStructures = (input.structureTags || []).map(prettyStructure).filter(Boolean);
  const structure = envStructures[0] || inputStructures[0] || '';
  const depthFact = Number.isFinite(depth) ? `${Math.round(depth)} m` : '';
  const reliefFact = Number.isFinite(relief) && relief > 0 ? `${Math.round(relief)} m relief` : '';
  const slopeFact = Number.isFinite(slope) ? `${slope.toFixed(1)}° slope` : slopeClass;

  const weatherFact = clean(weatherSignal?.summary);
  const facts = unique([
    currentFact,
    depthFact ? `${depthFact} water` : '',
    structure,
    reliefFact,
    weatherFact,
  ]).slice(0, 4);

  // Facts above are the raw Water Intel measurements. Everything below is
  // interpretation/action so the UI does not repeat the same numbers in every box.
  let where = '';
  if (bathymetry) {
    if (structure) {
      where = Number.isFinite(relief) && relief >= 8
        ? `Work the ${structure} and its break line first`
        : `Start on the ${structure} and nearby transition`;
    } else if (Number.isFinite(relief) && relief >= 8) {
      where = 'Work the strongest mapped break before featureless water';
    } else {
      where = 'Look for the best structural transition or isolated edge';
    }
  } else {
    where = inputStructures[0] ? `Start on ${inputStructures[0]}` : 'Use the best-confirmed habitat edge';
  }

  let when = '';
  if (current) {
    if (phase && lower(phase) === 'slack') {
      when = 'Slack-water window — slow down and pick apart the highest-percentage edge';
    } else if (Number.isFinite(speed) && speed >= 0.75) {
      when = 'Strong-flow window — favor protected seams, lee edges, and controlled presentations';
    } else if (Number.isFinite(speed) && speed >= 0.2) {
      when = 'Moving-water window — prioritize feeding lanes and current-facing structure';
    } else {
      when = 'Light-flow window — slow the presentation and cover structure carefully';
    }
  } else if (weatherAvailable) {
    if (weatherSignal?.lowLight) {
      when = 'Reduced-light window — check exposed edges and transition water before retreating to heavier cover';
    } else if (weatherSignal?.windClass === 'strong') {
      when = 'Wind-driven window — favor protected lanes and edges where the surface is still fishable';
    } else if (weatherSignal?.windClass === 'breezy') {
      when = 'Working-breeze window — check wind-affected edges and bait movement before calm pockets';
    } else {
      when = 'Use the strongest species activity window and let the live weather decide whether to stay or move';
    }
  } else {
    when = 'Fish the strongest species timing window and reassess as conditions change';
  }

  let approach = '';
  if (current && Number.isFinite(speed) && speed >= 0.75) {
    approach = structure
      ? `Set up on the protected or downstream side of the ${structure}; present across or with the flow instead of fighting it`
      : 'Use current seams and protected water; present across or with the flow instead of fighting it';
  } else if (current && Number.isFinite(speed) && speed >= 0.2) {
    approach = structure
      ? `Work the current-facing edge of the ${structure}, then sweep the downstream side for holding fish`
      : 'Work current-facing edges first, then the downstream seam';
  } else if (bathymetry && Number.isFinite(relief) && relief >= 8) {
    approach = structure
      ? `Cover the ${structure} and its break line before spending time over featureless water`
      : 'Cover the highest-relief break before spending time over featureless water';
  } else if (bathymetry) {
    approach = 'Cover the best structural transition first, then adjust depth or retrieve speed';
  } else {
    approach = 'Start with the highest-percentage habitat and make one controlled adjustment at a time';
  }

  const target = bathymetry
    ? [
        structure ? `Structure: ${structure}` : '',
        depthFact ? `Bottom depth: ${depthFact}` : '',
        reliefFact ? `Relief: ${reliefFact}` : '',
        slopeFact ? `Slope: ${slopeFact}` : '',
      ].filter(Boolean).join(' • ')
    : (structure ? `Structure: ${structure}` : 'Target the strongest confirmed habitat edge');

  const why = unique([
    currentFact ? `Current is ${currentFact}${strength && strength !== 'unknown' ? ` (${strength})` : ''}` : '',
    trend && lower(trend) !== 'unknown' ? `Current trend is ${trend.toLowerCase()}` : '',
    structure ? `Mapped structure highlights ${structure}` : '',
    depthFact ? `Bottom depth is about ${depthFact}` : '',
    reliefFact ? `Local relief is about ${reliefFact}` : '',
  ]).slice(0, 4);

  let rightNow = '';
  if (current && Number.isFinite(speed) && speed >= 0.75) {
    rightNow = structure
      ? `Strong flow is compressing the usable water around the ${structure}. Start on the protected side and work the seam.`
      : 'Strong flow is compressing the usable water. Start on protected seams before exposed water.';
  } else if (current && Number.isFinite(speed) && speed >= 0.2) {
    rightNow = structure
      ? `Moving water is sweeping the ${structure}. Fish the current-facing edge first, then the downstream seam.`
      : 'Moving water is creating feeding lanes. Work current-facing edges before the downstream seam.';
  } else if (current) {
    rightNow = structure && Number.isFinite(relief) && relief >= 8
      ? `Flow is light over a defined ${structure}. Fish are more likely to hold tight to the break than roam open water.`
      : structure
        ? `Flow is light around the ${structure}. Slow down and cover the transition carefully.`
        : 'Flow is light right now. Slow down and focus on the best available structure.';
  } else if (bathymetry && structure && Number.isFinite(relief) && relief >= 8) {
    rightNow = `The bottom changes sharply around this ${structure}. Focus the break before featureless water.`;
  } else if (bathymetry && structure) {
    rightNow = `This ${structure} is the clearest mapped target. Cover the transition before moving.`;
  } else if (bathymetry) {
    rightNow = 'The bottom is relatively subtle here. Look for small transitions or isolated structure.';
  } else if (weatherAvailable) {
    if (weatherSignal?.lowLight) {
      rightNow = 'Reduced light is widening the usable edge window. Check exposed transitions before defaulting to the deepest cover.';
    } else if (weatherSignal?.windClass === 'strong') {
      rightNow = 'Wind is the dominant live condition. Start on fishable protected edges and use wind-driven seams as search lanes.';
    } else if (weatherSignal?.windClass === 'breezy') {
      rightNow = 'A working breeze can organize bait and surface movement. Check the wind-affected edge, then compare it with nearby protected water.';
    }
  }

  return {
    active: true,
    confidence,
    confidenceLabel,
    facts,
    rightNow: rightNow || sentence(approach),
    where: sentence(where),
    when: sentence(when),
    approach: sentence(approach),
    target,
    bestBet: sentence(approach),
    why,
    ...(weatherSignal ? { weatherSignal } : {}),
    current: current ? {
      available: true,
      speedKnots: Number.isFinite(speed) ? speed : null,
      directionLabel: direction || null,
      strength: clean(current?.strength) || null,
      phase: phase || null,
      trend: trend || null,
      sourceLabel: clean(current?.sourceLabel || current?.providerLabel || current?.source) || null,
    } : null,
    bathymetry: bathymetry ? {
      available: true,
      depthMeters: Number.isFinite(depth) ? depth : null,
      localReliefMeters: Number.isFinite(relief) ? relief : null,
      slopeDegrees: Number.isFinite(slope) ? slope : null,
      slopeClass: slopeClass || null,
      structureTypes: envStructures,
      sourceLabel: clean(bathymetry?.sourceLabel || bathymetry?.providerLabel || bathymetry?.source) || null,
    } : null,
  };
}

export function buildFishIQSpeciesTacticalFusion(
  baseFusion: FishIQTacticalFusion | null | undefined,
  species: any,
): FishIQTacticalFusion | null {
  if (!baseFusion?.active) return baseFusion || null;

  const name = clean(species?.commonName || species?.name || species?.id || 'This species');
  const hay = [
    species?.commonName,
    species?.name,
    species?.id,
    species?.scientificName,
    species?.family,
    species?.category,
    species?.summary,
    ...(Array.isArray(species?.habitats) ? species.habitats : []),
    ...(Array.isArray(species?.structureTags) ? species.structureTags : []),
    ...(Array.isArray(species?.behaviorTags) ? species.behaviorTags : []),
    ...(Array.isArray(species?.bestTechniques) ? species.bestTechniques : []),
    ...(Array.isArray(species?.tactics?.bestTechniques) ? species.tactics.bestTechniques : []),
  ].map(lower).filter(Boolean).join(' ');

  const current = baseFusion.current?.available ? baseFusion.current : null;
  const bathymetry = baseFusion.bathymetry?.available ? baseFusion.bathymetry : null;
  const speed = Number(current?.speedKnots);
  const hasMovingWater = current && Number.isFinite(speed) && speed >= 0.2;
  const strongFlow = current && Number.isFinite(speed) && speed >= 0.75;
  const structure = prettyStructure(bathymetry?.structureTypes?.[0] || '');
  const relief = Number(bathymetry?.localReliefMeters);
  const hasRelief = Number.isFinite(relief) && relief >= 8;
  const weatherSignal = (baseFusion as any)?.weatherSignal || null;

  const isFlatfish = /halibut|flounder|sole|turbot|plaice|fluke/.test(hay);
  // Roaming/bait-current predators take precedence over generic reef/structure
  // words. This keeps California Yellowtail, amberjack, trevally, cobia, etc.
  // from being turned into bottom/reef fish merely because their catalog record
  // also mentions kelp, reefs, islands, or structure.
  const isPelagic = /tuna|bonito|mackerel|wahoo|mahi|dorado|marlin|sailfish|swordfish|billfish|bluewater|pelagic|yellowtail|amberjack|trevally|cobia|queenfish|rainbow runner|bait[- ]tracker|roaming predator/.test(hay);
  const isRockStructure = !isPelagic && /rockfish|lingcod|cabezon|grouper|snapper|sheepshead|sculpin|wrasse|reef/.test(hay);
  const isEstuaryAmbush = /redfish|red drum|snook|seatrout|speckled trout|striped bass|barramundi|tarpon|bonefish|croaker|corbina|estuary|mangrove/.test(hay);
  const isSalmonidCurrent = /trout|salmon|steelhead|char/.test(hay);
  const isFreshCover = /largemouth|smallmouth|crappie|bluegill|sunfish|perch|pike|muskie|musky|walleye|catfish|carp/.test(hay);

  let rightNow = baseFusion.rightNow;
  let where = baseFusion.where;
  let approach = baseFusion.approach;
  let bestBet = baseFusion.bestBet;
  const speciesWhy: string[] = [];

  if (isFlatfish) {
    rightNow = hasMovingWater
      ? `${name} can use this moving water as an ambush setup. Keep the presentation near bottom and work the break instead of roaming open water.`
      : `${name} is a bottom-oriented target here. Slow down and pick apart the strongest bottom transition.`;
    where = structure
      ? `Work the lower edge of the ${structure} and the adjacent bottom transition.`
      : hasRelief
        ? 'Work the foot of the relief and the nearby bottom transition.'
        : 'Stay tight to the best bottom transition rather than featureless water.';
    approach = hasMovingWater
      ? 'Keep the bait or lure near bottom and let it sweep naturally along the break.'
      : 'Use a slow bottom presentation and cover the edge methodically.';
    bestBet = approach;
    speciesWhy.push('Flatfish tactics prioritize bottom contact and ambush edges.');
  } else if (isRockStructure) {
    rightNow = hasRelief || structure
      ? `${name} should relate closely to the mapped relief. Fish tight to structure before expanding into open water.`
      : `${name} is structure-oriented. Start with the hardest edge or relief you can confirm.`;
    where = structure
      ? `Stay tight to the ${structure}, especially its face, crest, and break line.`
      : 'Target the highest-relief structure available.';
    approach = strongFlow
      ? 'Use controlled presentations on the protected side of the structure and keep contact through the strike zone.'
      : 'Make controlled near-structure or vertical passes before moving away from the relief.';
    bestBet = approach;
    speciesWhy.push('Structure fish are interpreted against relief before open-water movement.');
  } else if (isPelagic) {
    rightNow = hasMovingWater
      ? `${name} is an open-water target, so use the current and mapped break as a search edge rather than treating bottom depth as the target itself.`
      : `${name} is an open-water target. Use the mapped break as a reference, then look for bait and water-column activity.`;
    where = structure
      ? `Search the water column along and above the ${structure}, especially where current concentrates bait.`
      : 'Search current edges, bait concentrations, and water-column transitions.';
    approach = hasMovingWater
      ? 'Cover water along the current edge and adjust depth to bait or marks instead of pinning the presentation to bottom.'
      : 'Cover water and follow bait or marks; use bottom structure only as a reference edge.';
    bestBet = approach;
    speciesWhy.push('Pelagic tactics use current and structure as search edges, not automatic bottom targets.');
  } else if (isEstuaryAmbush) {
    rightNow = hasMovingWater
      ? `${name} can use this flow to pin forage against edges and mouths. Work the seam before broad open water.`
      : `${name} is an edge-and-ambush target here. Slow down around mouths, points, and protected transitions.`;
    where = structure
      ? `Work the ${structure} where it meets a current seam, mouth, point, or protected edge.`
      : 'Prioritize current seams, mouths, points, and protected shoreline edges.';
    approach = hasMovingWater
      ? 'Present across or slightly with the flow so the bait tracks naturally through the ambush lane.'
      : 'Make controlled passes through the edge before widening the search.';
    bestBet = approach;
    speciesWhy.push('Estuary ambush fish are interpreted around seams, mouths, and forage lanes.');
  } else if (isSalmonidCurrent) {
    rightNow = hasMovingWater
      ? `${name} is best read against current breaks. Focus on seams and softer holding water beside faster flow.`
      : `${name} is likely to favor stable holding water. Pick apart depth changes, shade, and the best available current break.`;
    where = structure
      ? `Fish the softer seam beside the ${structure} before the exposed face.`
      : 'Target seams, eddies, tailouts, and softer holding water beside the main flow.';
    approach = strongFlow
      ? 'Keep the presentation controlled in softer water and avoid racing it through the fastest lane.'
      : 'Present naturally through the seam and repeat the highest-percentage drift.';
    bestBet = approach;
    speciesWhy.push('Salmonid tactics emphasize holding seams and efficient current breaks.');
  } else if (isFreshCover) {
    rightNow = hasMovingWater
      ? `${name} should use the flow as an edge-maker. Start where current meets cover or a depth transition.`
      : `${name} is better treated as a cover-and-transition target than an open-water bottom-depth target.`;
    where = structure
      ? `Start where the ${structure} meets cover, shade, or a depth transition.`
      : 'Start on cover, shade, points, or the clearest depth transition.';
    approach = strongFlow
      ? 'Favor protected pockets and current breaks, then work outward only after the cover is picked apart.'
      : 'Work the highest-percentage cover first and change depth or retrieve speed one step at a time.';
    bestBet = approach;
    speciesWhy.push('Freshwater cover fish are interpreted around cover and transitions before raw depth.');
  }

  // Weather refines the tactical read only after species/water behavior is established.
  // It does not create species presence and it never overrides the regional/water guards.
  if (weatherSignal?.lowLight) {
    if (isEstuaryAmbush) {
      rightNow = `${rightNow} Reduced light can keep ambush edges useful longer, so check exposed seams before retreating to protected cover.`;
      speciesWhy.push('Reduced light supports an edge-and-ambush search window.');
    } else if (isFreshCover) {
      approach = `${approach} With reduced light, check outside cover edges before moving deep into shade.`;
      bestBet = approach;
      speciesWhy.push('Reduced light can expand the usable edge around cover.');
    } else if (isPelagic) {
      approach = `${approach} Use the lower-light window to scan for bait or surface activity along the search edge.`;
      bestBet = approach;
      speciesWhy.push('Reduced light is treated as a search-window cue, not a presence signal.');
    } else if (isSalmonidCurrent) {
      approach = `${approach} Reduced light makes the softer edge worth checking before the brightest exposed lane.`;
      bestBet = approach;
      speciesWhy.push('Reduced light refines the holding-water search.');
    }
  }

  if (weatherSignal?.windClass === 'strong') {
    approach = `${approach} Strong wind makes boat/shore control part of the decision; favor fishable protected lanes before exposed water.`;
    bestBet = approach;
    speciesWhy.push('Strong wind shifts the tactical emphasis toward controllable water.');
  } else if (weatherSignal?.windClass === 'breezy' && (isPelagic || isEstuaryAmbush)) {
    approach = `${approach} Compare the wind-affected edge with the protected side for concentrated bait or cleaner presentation lanes.`;
    bestBet = approach;
    speciesWhy.push('A working breeze adds a useful edge comparison.');
  }

  return {
    ...baseFusion,
    rightNow: sentence(rightNow || ''),
    where: sentence(where || ''),
    approach: sentence(approach || ''),
    bestBet: sentence(bestBet || approach || ''),
    why: unique([...(baseFusion.why || []), ...speciesWhy]).slice(0, 4),
  };
}

export function buildFishIQMovementTags(input: FishIQMovementReadInput): string[] {
  const { profile } = input;
  const structureText = (input.structureTags || []).map(lower).join(' ');
  const tags: string[] = [];
  if (profile.timeOfDay === 'dawn' || profile.timeOfDay === 'dusk') tags.push('active during low light');
  if (profile.timeOfDay === 'day' && profile.season === 'summer') tags.push('sliding to shade');
  if (profile.timeOfDay === 'night') tags.push('working edges after dark');
  if (profile.waterType === 'saltwater' || profile.waterType === 'brackish') tags.push('moving with tide/current');
  if (/current|seam|bend|eddy|channel|mouth/.test(structureText)) tags.push('staging near current breaks');
  if (/drop|ledge|point|reef|rock/.test(structureText)) tags.push('holding near structure edges');
  if (/flat|grass|weed|shoreline|cover|dock|shade/.test(structureText)) tags.push('using cover and ambush lanes');

  const current = input.environment?.current?.available === true ? input.environment.current : null;
  const speed = Number(current?.speedKnots);
  if (current && Number.isFinite(speed) && speed >= 0.75) tags.push('using protected seams in stronger flow');
  else if (current && Number.isFinite(speed) && speed >= 0.2) tags.push('feeding along current-facing edges');
  else if (current) tags.push('slowing down in light flow');

  const relief = Number(input.environment?.bathymetry?.localReliefMeters);
  if (Number.isFinite(relief) && relief >= 20) tags.push('keying on high-relief breaks');
  else if (Number.isFinite(relief) && relief >= 8) tags.push('working break lines');

  if (profile.season === 'spring') tags.push('seasonal staging read');
  if (profile.season === 'fall') tags.push('following forage transitions');
  if (profile.season === 'winter') tags.push('holding tighter to stable water');
  return unique(tags).slice(0, 7);
}

export function buildFishIQSpeciesPredictions(input: FishIQMovementReadInput): FishIQSpeciesPrediction[] {
  const species = Array.isArray(input.species) ? input.species : [];
  const movementTags = buildFishIQMovementTags(input);
  const bestWindow = buildFishIQBestWindow(input.profile, species);
  return species.slice(0, 12).map((item, index) => {
    const rank = item?.__fishiq?.locationRank || null;
    const confidenceBreakdown = buildFishIQSpeciesConfidence({
      species: item,
      areaEvidence: input.evidence || null,
      environment: input.environment || null,
    });
    const confidence = confidenceBreakdown.score;
    const movement = clean(item?.__fishiq?.bestMove || movementTags[index % Math.max(1, movementTags.length)] || movementTags[0]);
    const reason = clean(item?.__fishiq?.whyHere || item?.__fishiq?.whyNow || (Array.isArray(rank?.reasons) ? rank.reasons.slice(0, 2).join(' • ') : '') || item?.summary || '');

    const canonicalSpeciesId = clean(
      item?.canonicalSpeciesId ||
      item?.canonical?.id ||
      item?.canonicalMetadata?.canonicalSpeciesId ||
      item?.id ||
      item?.slug,
    );
    const scientificName = clean(item?.scientificName || item?.taxonomy?.scientificName);
    const speciesId = clean(item?.id || item?.slug || canonicalSpeciesId);

    return {
      name: clean(item?.commonName || item?.name || item?.id || 'Species'),
      emoji: fishIQSpeciesEmoji(item),
      confidence,
      confidenceLabel: confidenceBreakdown.label,
      confidenceBreakdown,
      canonicalSpeciesId: canonicalSpeciesId || undefined,
      speciesId: speciesId || undefined,
      scientificName: scientificName || undefined,
      movement: movement || undefined,
      bestWindow,
      reason: reason || undefined,
    } as FishIQSpeciesPrediction;
  });
}
